/**
 * <s-launch-demo>: "Three ways to launch", shown not told. Buttons [data-pick]
 * choose a tier; the matching [data-opt] opens (data-active), the sample store
 * [data-demo-store] switches data-stage and replays its build (data-play), and
 * the host mirrors the choice as data-choice (the compare table highlights it).
 *
 * While the section is on screen it auto-plays through the tiers (a progress
 * line runs on the active option), pausing on hover/focus; the first click
 * stops auto-play for good. Reduced motion: no auto-play, no build animation.
 * The choice is announced through [data-pick-status] (data-msg-<tier>).
 * The explainer [data-demo-info] follows the mouse as a tooltip (data-follow, --ix/--iy) or docks via [data-info-toggle] (host data-info-open; Esc closes).
 */
class SLaunchDemo extends HTMLElement {
  static DWELL = 7000;
  #tiers = [];
  #timer = 0;
  #visible = false;
  #paused = false;
  #user = false;
  #observer = null;
  #panel = null;
  #panelReq = 0;
  #last = null;
  #reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  #onClick = (event) => {
    const info = event.target.closest('[data-info-toggle]');
    if (info && this.contains(info)) {
      const open = !this.hasAttribute('data-info-open');
      this.toggleAttribute('data-info-open', open);
      info.setAttribute('aria-expanded', String(open));
      if (this.#panel) {
        this.#panel.toggleAttribute('data-show', open);
        const box = info.getBoundingClientRect();
        this.#last = { x: box.left - 12, y: box.bottom - 12 };
        if (open) this.#place();
      }
      this.#user = true;
      this.#stop();
      return;
    }
    const slide = event.target.closest('[data-slide]');
    if (slide && this.contains(slide)) {
      this.#user = true;
      this.#stop();
      const track = slide.closest('[data-slider]')?.querySelector('[data-slider-track]');
      const rtl = getComputedStyle(this).direction === 'rtl' ? -1 : 1;
      track?.scrollBy({ left: Number(slide.dataset.slide) * track.clientWidth * 0.8 * rtl, behavior: this.#reduced ? 'auto' : 'smooth' });
      return;
    }
    const button = event.target.closest('[data-pick]');
    if (!button || !this.contains(button)) return;
    this.#user = true;
    this.#stop();
    this.#select(button.dataset.pick, true);
  };
  #onEnter = () => { this.#paused = true; this.#stop(); };
  #onLeave = () => { this.#paused = false; this.#schedule(); };

  connectedCallback() {
    this.#tiers = [...this.querySelectorAll('[data-pick]')].map((button) => button.dataset.pick);
    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !this.hasAttribute('data-info-open')) return;
      this.removeAttribute('data-info-open');
      this.#panel?.removeAttribute('data-show');
      const toggle = this.querySelector('[data-info-toggle]');
      toggle?.setAttribute('aria-expanded', 'false');
      toggle?.focus();
    });
    const options = this.querySelector('[data-opt]')?.parentElement;
    options?.addEventListener('pointerenter', this.#onEnter);
    options?.addEventListener('pointerleave', this.#onLeave);
    options?.addEventListener('focusin', this.#onEnter);
    options?.addEventListener('focusout', this.#onLeave);
    const frame = this.querySelector('[data-demo-frame], [data-compare-panel]');
    frame?.addEventListener('pointerenter', this.#onEnter);
    frame?.addEventListener('pointerleave', this.#onLeave);
    frame?.addEventListener('focusin', this.#onEnter);
    frame?.addEventListener('focusout', this.#onLeave);
    if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) this.#portal(frame);
    for (const track of this.querySelectorAll('[data-slider-track]')) {
      track.addEventListener('scroll', () => this.#edges(track), { passive: true });
      track.addEventListener('touchstart', () => { this.#user = true; this.#stop(); }, { passive: true });
      this.#edges(track);
    }

    if (this.#reduced || !('IntersectionObserver' in window)) return;
    this.setAttribute('data-motion', '');
    this.#observer = new IntersectionObserver(([entry]) => {
      const was = this.#visible;
      this.#visible = entry.isIntersecting;
      if (this.#visible && !was) this.#replay();
      if (this.#visible) this.#schedule();
      else this.#stop();
    }, { threshold: 0.35 });
    this.#observer.observe(this.querySelector('[data-demo-store]') ?? this);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.#observer?.disconnect();
    if (this.#panel?.hasAttribute('data-portal')) this.#panel.remove();
    this.#stop();
  }


  /**
   * Mouse devices: the explainer becomes a cursor tooltip. It is moved to <body> (position: fixed) so it can
   * follow the pointer beyond the graphic's edges; it mirrors data-choice for its tier styling.
   */
  #portal(frame) {
    const panel = this.querySelector('[data-demo-info]');
    if (!panel || !frame) return;
    this.#panel = panel;
    panel.setAttribute('data-portal', '');
    panel.dataset.choice = this.dataset.choice ?? '';
    document.body.append(panel);
    frame.addEventListener('pointermove', (event) => {
      if (event.pointerType !== 'mouse') return;
      this.#last = { x: event.clientX, y: event.clientY };
      panel.setAttribute('data-show', '');
      if (!this.#panelReq) this.#panelReq = requestAnimationFrame(() => this.#place());
    }, { passive: true });
    frame.addEventListener('pointerleave', () => {
      if (!this.hasAttribute('data-info-open')) panel.removeAttribute('data-show');
      this.#preview(null);
    });
    // Hovering a theme logo loads that theme's banner at the top of the tooltip.
    frame.addEventListener('pointerover', (event) => {
      if (event.pointerType !== 'mouse') return;
      const tile = event.target instanceof Element ? event.target.closest('[data-preview]') : null;
      if (tile) this.#preview(tile);
    });
    frame.addEventListener('pointerout', (event) => {
      const from = event.target instanceof Element ? event.target.closest('[data-preview]') : null;
      const to = event.relatedTarget instanceof Element ? event.relatedTarget.closest('[data-preview]') : null;
      if (from && from !== to && !to) this.#preview(null);
    });
  }

  /** Show (tile) or hide (null) the theme banner at the top of the cursor tooltip. */
  #preview(tile) {
    const figure = this.#panel?.querySelector('[data-info-preview]');
    const img = figure?.querySelector('img');
    if (!figure || !img) return;
    if (!tile) {
      this.#panel.removeAttribute('data-previewing');
      figure.hidden = true;
      if (!this.#panelReq) this.#panelReq = requestAnimationFrame(() => this.#place());
      return;
    }
    const src = tile.dataset.preview ?? '';
    figure.style.setProperty('--pv-ratio', tile.dataset.previewRatio || '1.6');
    figure.removeAttribute('data-loaded');
    if (img.getAttribute('src') !== src) {
      img.onload = () => {
        figure.setAttribute('data-loaded', '');
        if (!this.#panelReq) this.#panelReq = requestAnimationFrame(() => this.#place());
      };
      img.src = src;
    }
    if (img.complete && img.naturalWidth) figure.setAttribute('data-loaded', '');
    figure.hidden = false;
    this.#panel.setAttribute('data-previewing', '');
    if (!this.#panelReq) this.#panelReq = requestAnimationFrame(() => this.#place());
  }

  #place() {
    this.#panelReq = 0;
    const panel = this.#panel;
    const point = this.#last;
    if (!panel || !point) return;
    const w = panel.offsetWidth;
    const h = panel.offsetHeight;
    const gap = 20;
    const edge = 12;
    let x = point.x + gap;
    let y = point.y + gap;
    if (x + w > window.innerWidth - edge) x = point.x - w - gap;
    if (y + h > window.innerHeight - edge) y = point.y - h - gap;
    x = Math.max(edge, Math.min(x, window.innerWidth - w - edge));
    y = Math.max(edge, Math.min(y, window.innerHeight - h - edge));
    panel.style.setProperty('--ix', `${Math.round(x)}px`);
    panel.style.setProperty('--iy', `${Math.round(y)}px`);
  }

  /** Hide prev/next at the ends of a track. */
  #edges(track) {
    const slider = track.closest('[data-slider]');
    if (!slider) return;
    const max = track.scrollWidth - track.clientWidth;
    const pos = Math.abs(track.scrollLeft);
    slider.toggleAttribute('data-at-start', pos <= 2);
    slider.toggleAttribute('data-at-end', pos >= max - 2);
  }

  #schedule() {
    this.#stop();
    if (this.#user || this.#paused || !this.#visible || this.#reduced) return;
    const active = this.querySelector('[data-opt][data-active]');
    active?.setAttribute('data-timing', '');
    this.#timer = window.setTimeout(() => {
      const index = this.#tiers.indexOf(this.dataset.choice);
      this.#select(this.#tiers[(index + 1) % this.#tiers.length], false);
      this.#schedule();
    }, SLaunchDemo.DWELL);
  }

  #stop() {
    window.clearTimeout(this.#timer);
    for (const option of this.querySelectorAll('[data-opt][data-timing]')) option.removeAttribute('data-timing');
  }

  #replay() {
    const store = this.querySelector('[data-demo-store]');
    if (!store || this.#reduced) return;
    store.removeAttribute('data-play');
    void store.offsetWidth;
    store.setAttribute('data-play', '');
  }

  #select(tier, announce) {
    if (!tier) return;
    if (announce) {
      // Share a real pick (not auto-play) with the closing section (<s-closing>), which tailors its last word to it.
      try { sessionStorage.setItem("the4-tier", tier); } catch {}
      document.dispatchEvent(new CustomEvent("the4:tier", { detail: tier }));
    }
    this.dataset.choice = tier;
    for (const button of this.querySelectorAll('[data-pick]')) {
      button.setAttribute('aria-pressed', String(button.dataset.pick === tier));
    }
    for (const option of this.querySelectorAll('[data-opt]')) {
      option.toggleAttribute('data-active', option.dataset.opt === tier);
      option.querySelector('[data-opt-body]')?.toggleAttribute('inert', option.dataset.opt !== tier);
    }
    const store = this.querySelector('[data-demo-store]');
    if (store) store.dataset.stage = tier;
    if (this.#panel) this.#panel.dataset.choice = tier;
    for (const slider of this.querySelectorAll('[data-slider]')) {
      const on = slider.dataset.slider === tier;
      slider.hidden = !on;
      const track = slider.querySelector('[data-slider-track]');
      if (on && track) { track.scrollLeft = 0; this.#edges(track); }
    }
    this.#replay();
    if (!announce) return;
    const status = this.querySelector('[data-pick-status]');
    const key = `msg${tier[0].toUpperCase()}${tier.slice(1)}`;
    if (status) status.textContent = status.dataset[key] ?? '';
  }
}

if (!customElements.get('s-launch-demo')) customElements.define('s-launch-demo', SLaunchDemo);
